import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as api from "../services/api";
import * as f from "../utils/formatter";
import { useLoaderData, useRevalidator } from "react-router";
import { useTranslation } from "react-i18next";
import { workingDays, workingHoursInDay } from "shared/src/calendar.mjs";
import ButtonLoader from "../components/ButtonLoader";
import { formData, FormErrorList, useFormAction } from "../form";
import { assocInIfUnset, indexBy, updateInIfExists } from "shared/src/util.mjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { startOfDay } from "shared/src/date.mjs";
import DateRangePicker from "../components/mui/DateRangePicker";
import AutocompleteSelect from "../components/AutocompleteSelect";
import { t } from "i18next";
import TimeRangePicker from "../components/mui/TimeRangePicker";
import { useRoles } from "../context/AuthenticationTokenContext.jsx";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useSnackbarMessage } from "../components/Snackbar.jsx";

export async function loader({ params, request }) {
  const res = await api.loadLocationEditData(
    {
      id: params.id,
      calendarFilter: { location_id: { eq: params.id }, visible: { eq: true } },
    },
    api.contextFromRequest(request),
  );
  if (res.data.location == null) {
    throw new Response("Not Found", { status: 404, statusText: "Not Found" });
  }
  return res;
}

function NewWorkingHourForm({ day }) {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <DateRangePicker
        autoFocus
        nameFrom={`working_hours.${day}.valid_from`}
        nameTo={`working_hours.${day}.valid_to`}
        disablePast
        slotProps={{
          textField: {
            label: "Validní od - do",
            required: true,
            fullWidth: true,
          },
        }}
      />
      <TimeRangePicker
        nameFrom={`working_hours.${day}.from`}
        nameTo={`working_hours.${day}.to`}
        slotProps={{
          textField: {
            label: "Od - do",
            required: true,
            fullWidth: true,
          },
        }}
      />
    </Box>
  );
}

function WorkingHourDialog({ day, location_id, setEditDay }) {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchLocation(formConverter(formData(e.currentTarget))),
    () => {
      revalidator.revalidate();
      setEditDay(null);
      displaySnackbarMessage({
        title: t("Otevírací doba byla úspěšně změněna."),
      });
    },
  );

  return (
    <Dialog open={true} onClose={() => setEditDay(null)}>
      <DialogTitle>{t(`date.day_${day}`)}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <input name="id" type="hidden" value={location_id} />
          <NewWorkingHourForm key={day} day={day} />
          <FormErrorList formErrors={errors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditDay(null)}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function atStartOfDay(dateS) {
  return `${dateS}T00:00:00`;
}

function convertWorkingHour(wh) {
  return {
    ...wh,
    valid_from: atStartOfDay(wh.valid_from),
    valid_to: atStartOfDay(wh.valid_to),
  };
}

function formConverter(formData) {
  return updateInIfExists(formData, ["working_hours"], (working_hours) => {
    return Object.fromEntries(
      Object.entries(working_hours).map(([day, wh]) => [
        day,
        [convertWorkingHour(wh)],
      ]),
    );
  });
}

function CreateBlocationDialog({ onClose, location, calendars }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const displaySnackbarMessage = useSnackbarMessage();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.createLocationBlocation(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      displaySnackbarMessage({
        title: t("Blokace byla úspěšně vytvořena."),
      });
      onClose();
    },
  );

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Nová blokace</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="location_id" value={location.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <TextField required name="title" label="Název" />
            <DateRangePicker
              nameFrom="valid_from"
              nameTo="valid_to"
              disablePast
              slotProps={{
                textField: () => ({
                  label: "Validní od ~ do",
                  required: true,
                }),
              }}
            />
            <AutocompleteSelect
              multiple
              label="Validní dny"
              name="days"
              options={workingDays.map((day) => ({ id: day, name: day }))}
            />
            <TimeRangePicker
              nameFrom="from"
              nameTo="to"
              slotProps={{
                textField: { label: "Blokace od ~ do", required: true },
              }}
            />
            <AutocompleteSelect
              multiple
              label="Kalendáře"
              name="calendars"
              options={calendars.map((c) => ({ id: c.id, name: c.name }))}
              required
            />
            <FormControlLabel
              name="exceptional"
              control={<Checkbox />}
              label="Vyjímečná"
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function blocationFormConverter(formData) {
  return assocInIfUnset(formData, ["days"], []);
}

function EditBlocationDialog({ onClose, blocation, calendars }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const displaySnackbarMessage = useSnackbarMessage();
  const { loading, errors, onSubmit } = useFormAction(
    (e) =>
      api.patchLocationBlocation(
        blocationFormConverter(formData(e.currentTarget)),
      ),
    () => {
      revalidator.revalidate();
      onClose();
      displaySnackbarMessage({
        title: t("Blokace byla úspěšně změněna."),
      });
    },
  );

  const calendarById = indexBy(calendars, (c) => c.id);

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{blocation.title}</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="id" value={blocation.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <TextField
              required
              name="title"
              label="Název"
              defaultValue={blocation.title}
            />
            <DateRangePicker
              nameFrom="valid_from"
              nameTo="valid_to"
              slotProps={{
                textField: () => ({
                  label: "Validní od - do",
                  required: true,
                }),
              }}
              defaultValue={[blocation.valid_from, blocation.valid_to]}
            />
            <AutocompleteSelect
              multiple
              label="Validní dny"
              name="days"
              options={workingDays.map((day) => ({
                id: day,
                name: t(`date.day_${day}`),
              }))}
              defaultValue={blocation.days.map((day) => ({
                id: day,
                name: t(`date.day_${day}`),
              }))}
            />
            <TimeRangePicker
              nameFrom="from"
              nameTo="to"
              slotProps={{
                textField: { label: "Blokace od ~ do", required: true },
              }}
              defaultValue={[blocation.from, blocation.to]}
            />
            <AutocompleteSelect
              multiple
              label="Kalendáře"
              name="calendars"
              options={calendars.map((c) => ({ id: c.id, name: c.name }))}
              defaultValue={blocation.calendars
                .map((c) => {
                  const calendar = calendarById[c.calendar_id];
                  if (calendar == null) {
                    return;
                  }

                  return { id: calendar.id, name: calendar.name };
                })
                .filter((o) => o != null)}
              required
            />
            <FormControlLabel
              name="exceptional"
              control={<Checkbox defaultChecked={blocation.exceptional} />}
              label="Vyjímečná"
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function BooleanValue({ value }) {
  return value ? (
    <FontAwesomeIcon icon={faCheck} />
  ) : (
    <FontAwesomeIcon icon={faXmark} />
  );
}

function BlocationsTable({ location, calendars }) {
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [editedBlocation, setEditedBlocation] = React.useState(null);
  const [deleteBlocations, setDeleteBlocations] = React.useState(new Set());
  const revalidator = useRevalidator();
  const displaySnackbarMessage = useSnackbarMessage();
  const calendarById = indexBy(calendars, (c) => c.id);
  const roles = useRoles();
  const canEditBlocations = isGrantedAny(
    roles,
    new Set([role.Floor_Manager, role.Manager_Clinic]),
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h2">Blokace</Typography>
        {canEditBlocations && (
          <Button
            sx={{ marginBottom: 2 }}
            variant="outlined"
            onClick={() => setShowCreateDialog(true)}
          >
            Nová blokace
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Název</TableCell>
            <TableCell>Blokace</TableCell>
            <TableCell>Dny</TableCell>
            <TableCell>Kalendáře</TableCell>
            <TableCell>Vyjímečná</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {location.blocations.map((blocation) => {
            const deleting = deleteBlocations.has(blocation.id);

            return (
              <TableRow key={blocation.id}>
                <TableCell>{blocation.title}</TableCell>
                <TableCell>
                  {f.time24(blocation.from)} - {f.time24(blocation.to)} (
                  {f.dateRange(blocation.valid_from, blocation.valid_to)})
                </TableCell>
                <TableCell>
                  {blocation.days.map((d) => t(`date.day_${d}`)).join(", ")}
                </TableCell>
                <TableCell>
                  {blocation.calendars
                    .map((c) => calendarById[c.calendar_id])
                    .filter((c) => c != null)
                    .map((c) => c.name)
                    .join(", ")}
                </TableCell>
                <TableCell>
                  <BooleanValue value={blocation.exceptional} />
                </TableCell>
                <TableCell>
                  {canEditBlocations && (
                    <>
                      <IconButton onClick={() => setEditedBlocation(blocation)}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </IconButton>
                      <IconButton
                        disabled={deleting}
                        onClick={async () => {
                          try {
                            setDeleteBlocations((prev) => {
                              const current = new Set(prev);
                              current.add(blocation.id);

                              return current;
                            });
                            await api.deleteLocationBlocation(blocation.id);
                            revalidator.revalidate();
                          } finally {
                            setDeleteBlocations((prev) => {
                              const current = new Set(prev);
                              current.delete(blocation.id);

                              return current;
                            });
                            displaySnackbarMessage({
                              title: t("Blokace byla úspěšně zrušena."),
                            });
                          }
                        }}
                      >
                        {deleting && <ButtonLoader />}
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {showCreateDialog && canEditBlocations && (
        <CreateBlocationDialog
          location={location}
          calendars={calendars}
          onClose={() => setShowCreateDialog(false)}
        />
      )}
      {editedBlocation && canEditBlocations && (
        <EditBlocationDialog
          blocation={editedBlocation}
          calendars={calendars}
          onClose={() => setEditedBlocation(null)}
        />
      )}
    </>
  );
}

function CreateClosureDialog({ onClose, location }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.createLocationClosure(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      onClose();
    },
  );

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Nové uzavření</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="location_id" value={location.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <DateRangePicker
              nameFrom="valid_from"
              nameTo="valid_to"
              slotProps={{
                textField: () => ({
                  label: "Validní od ~ do",
                  required: true,
                }),
              }}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function EditClosureDialog({ onClose, closure }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchLocationClosure(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      onClose();
    },
  );

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Uzavření</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="id" value={closure.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <DateRangePicker
              nameFrom="valid_from"
              nameTo="valid_to"
              slotProps={{
                textField: () => ({
                  label: "Validní od - do",
                  required: true,
                }),
              }}
              defaultValue={[closure.valid_from, closure.valid_to]}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ClosuresTable({ location }) {
  const roles = useRoles();
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [editedClosure, setEditedClosure] = React.useState(null);
  const [deleteClosures, setDeleteClosures] = React.useState(new Set());
  const revalidator = useRevalidator();
  const canEditClosure = isGrantedAny(roles, new Set([role.Manager_Clinic]));

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Uzavření pobočky
        </Typography>
        {canEditClosure && (
          <Button
            sx={{ marginBottom: 2 }}
            variant="outlined"
            onClick={() => setShowCreateDialog(true)}
          >
            Nové uzavření
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Uzavření</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {location.closures.map((closure) => {
            const deleting = deleteClosures.has(closure.id);

            return (
              <TableRow key={closure.id}>
                <TableCell>
                  {f.dateRange(closure.valid_from, closure.valid_to)}
                </TableCell>
                <TableCell>
                  {canEditClosure && (
                    <>
                      <IconButton onClick={() => setEditedClosure(closure)}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </IconButton>
                      <IconButton
                        disabled={deleting}
                        onClick={async () => {
                          try {
                            setDeleteClosures((prev) => {
                              const current = new Set(prev);
                              current.add(closure.id);

                              return current;
                            });
                            await api.deleteLocationClosure(closure.id);
                            revalidator.revalidate();
                          } finally {
                            setDeleteClosures((prev) => {
                              const current = new Set(prev);
                              current.delete(closure.id);

                              return current;
                            });
                          }
                        }}
                      >
                        {deleting && <ButtonLoader />}
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {showCreateDialog && (
        <CreateClosureDialog
          location={location}
          onClose={() => setShowCreateDialog(false)}
        />
      )}
      {editedClosure && (
        <EditClosureDialog
          closure={editedClosure}
          onClose={() => setEditedClosure(null)}
        />
      )}
    </>
  );
}

function OpeningHoursTable({ location }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [editDay, setEditDay] = React.useState();
  const roles = useRoles();
  const canEditHours = isGrantedAny(roles, new Set([role.Manager_Clinic]));

  return (
    <>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Otevírací doba
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
                sx={{ aspectRatio: "1 / 1", mr: 0.5 }}
              >
                <FontAwesomeIcon
                  icon={open ? faChevronUp : faChevronDown}
                  size="xs"
                  fixedWidth
                />
              </IconButton>
              {t("resources.day")}
            </TableCell>
            <TableCell>{t("resources.workingHours")}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workingDays.map((day) => {
            const workingHours = workingHoursInDay(
              startOfDay(new Date()),
              location.working_hours[day],
            );

            return (
              <React.Fragment key={day}>
                <TableRow sx={{ "& > td": { borderBottom: "unset" } }}>
                  <TableCell>{t(`date.day_${day}`)}</TableCell>
                  <TableCell>
                    {workingHours &&
                      `${workingHours.from} - ${workingHours.to} (${f.dateRange(workingHours.valid_from, workingHours.valid_to)})`}
                  </TableCell>
                  <TableCell>
                    {canEditHours && (
                      <IconButton onClick={() => setEditDay(day)}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ paddingBottom: 0, paddingTop: 0 }}
                  >
                    <Collapse in={open}>
                      <Box margin={1}>
                        <Table>
                          <TableBody>
                            {location.working_hours[day]?.map((wh, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  {wh.from} - {wh.to} (
                                  {f.dateRange(wh.valid_from, wh.valid_to)})
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {editDay && (
        <WorkingHourDialog
          location_id={location.id}
          day={editDay}
          setEditDay={setEditDay}
        />
      )}
    </>
  );
}

export default function LocationEdit() {
  const loaderData = useLoaderData();
  const location = loaderData.data.location;
  const calendars = loaderData.data.calendars.data;

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2 }}>
        Pobočka {location.name}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <OpeningHoursTable location={location} />
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <BlocationsTable location={location} calendars={calendars} />
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <ClosuresTable location={location} />
        </CardContent>
      </Card>
    </>
  );
}
